/**
 * Top-level route table.
 *
 * Every screen the old AppContainer state machine could reach now has a
 * stable URL. Routes are arranged so:
 *
 *   /                       MainMenu landing
 *   /vs-computer            VsComputerConfig
 *   /local                  LocalMultiplayerConfig
 *   /online                 LobbyView
 *   /online/:gameId         OnlineGame (active match)
 *   /puzzles                PuzzleModeSelect (Solve / Endless / Watch picker)
 *   /puzzles/solve          PuzzleConfig (manual)
 *   /puzzles/solve/play     App in single-puzzle mode (solve)
 *   /puzzles/watch          PuzzleConfig (LLM)
 *   /puzzles/watch/play     App in single-puzzle mode (watch)
 *   /puzzles/endless        EndlessPuzzleConfig
 *   /puzzles/endless/play   App in endless mode
 *   /play                   App in local/vs-computer mode (config via state)
 *   /tutorial/quickstart    Tutorial (genesis)
 *   /tutorial/full          Tutorial (full)
 *   /leaderboard            Leaderboard
 *   /pricing                Pricing
 *   /admin                  AdminDashboard
 *   *                       redirect to /
 *
 * The active-puzzle URLs carry their config in search params (see
 * puzzleParams.js) so any one of them is shareable as a standalone link
 * that reconstructs the exact puzzle on a cold paste.
 */
import React from 'react';
import { createBrowserRouter } from 'react-router-dom';

import {
  MenuRoute,
  TutorialRoute,
  PlayRoute,
  PuzzlePlayRoute,
  OnlineGameRoute,
  AdminRoute,
  AdminLegacyRedirect,
  NotFoundRoute,
} from './routes';

export const router = createBrowserRouter([
  // The root wraps MenuRoute in AdminLegacyRedirect so bookmarks pointing at
  // `/?admin=1` or `/#admin` still arrive at /admin without manual cleanup.
  { path: '/', element: <AdminLegacyRedirect><MenuRoute scene="main" /></AdminLegacyRedirect> },

  { path: '/vs-computer', element: <MenuRoute scene="vs_computer" /> },
  { path: '/local',       element: <MenuRoute scene="local_multiplayer" /> },
  { path: '/online',      element: <MenuRoute scene="online_multiplayer" /> },

  { path: '/puzzles',          element: <MenuRoute scene="puzzle_mode_select" /> },
  { path: '/puzzles/solve',    element: <MenuRoute scene="puzzle" attemptMode="manual" /> },
  { path: '/puzzles/watch',    element: <MenuRoute scene="puzzle" attemptMode="llm" /> },
  { path: '/puzzles/endless',  element: <MenuRoute scene="endless_puzzle_config" /> },

  { path: '/puzzles/solve/play',   element: <PuzzlePlayRoute mode="solve" /> },
  { path: '/puzzles/watch/play',   element: <PuzzlePlayRoute mode="watch" /> },
  { path: '/puzzles/endless/play', element: <PuzzlePlayRoute mode="endless" /> },

  { path: '/play',            element: <PlayRoute /> },
  { path: '/online/:gameId',  element: <OnlineGameRoute /> },

  { path: '/tutorial/quickstart', element: <TutorialRoute type="genesis" /> },
  { path: '/tutorial/full',       element: <TutorialRoute type="full" /> },

  { path: '/leaderboard', element: <MenuRoute scene="leaderboard" /> },
  { path: '/pricing',     element: <MenuRoute scene="pricing" /> },

  { path: '/admin', element: <AdminRoute /> },

  { path: '*', element: <NotFoundRoute /> },
]);
